/* ============================================================
   MAIN PRODUCT V2 — interactive bits
   - Gallery thumbs + lightbox
   - Variant/size picker (sync to <product-form> hidden id input)
   - Quantity stepper
   - Detail tabs (Specs / Storage / Downloads / FAQ)
   - FAQ accordion
   - Size guide slider + table
   - Sticky buy bar
   - Related carousel nav
   ============================================================ */
(function () {
  'use strict';

  // ----- Gallery -----
  // Clean up any stray lightboxes left behind by prior IIFE runs (theme
  // editor reloads, ajax re-renders). Without this, opening the lightbox
  // after a re-init would leave multiple stacked overlays — closing one
  // wouldn't be enough.
  document.querySelectorAll('.pv2-lightbox').forEach(function (el) { el.remove(); });

  document.querySelectorAll('[data-pv2-gallery]').forEach(function (gallery) {
    // Idempotency guard: see [data-pv2-buy] for rationale.
    if (gallery.dataset.pv2GalleryInited === '1') return;
    gallery.dataset.pv2GalleryInited = '1';

    var thumbs = gallery.querySelectorAll('[data-pv2-thumb]');
    var slides = gallery.querySelectorAll('[data-pv2-slide]');
    var main = gallery.querySelector('[data-pv2-gallery-main]');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function activate(i) {
      thumbs.forEach(function (t, idx) { t.classList.toggle('is-active', idx === i); });
      slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === i); });
      gallery.querySelectorAll('[data-pv2-gallery-dot]').forEach(function (d, idx) {
        d.classList.toggle('is-active', idx === i);
      });

      var active = slides[i];
      gallery.classList.toggle('has-active-video', !!(active && active.querySelector('video')));
      slides.forEach(function (slide, idx) {
        var video = slide.querySelector('video');
        if (!video) return;
        if (idx !== i || reducedMotion.matches) {
          video.pause();
          if (idx !== i) {
            try { video.currentTime = 0; } catch (error) { /* Video not ready yet. */ }
          }
        } else {
          var playback = video.play();
          if (playback && typeof playback.catch === 'function') playback.catch(function () {});
        }
        video.controls = reducedMotion.matches;
      });
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { activate(i); });
    });

    if (main) {
      main.addEventListener('click', function () {
        var active = gallery.querySelector('[data-pv2-slide].is-active');
        if (!active) return;
        if (active.querySelector('video')) return;
        var src = active.getAttribute('data-pv2-fullsrc') || (active.querySelector('img') && active.querySelector('img').src);
        if (!src) return;
        openLightbox(src);
      });
    }

    /* NR4-M2 mobile swipe gallery wiring.
       The dots only exist when there are >1 slides, and the swipe-snap
       layout only applies at <=600px viewport. We always wire the JS;
       desktop hides the dots and uses the normal opacity-fade gallery,
       so the scroll/click handlers below are inert on desktop. */
    var dotsContainer = gallery.querySelector('[data-pv2-gallery-dots]');
    if (dotsContainer && main) {
      var dots = dotsContainer.querySelectorAll('[data-pv2-gallery-dot]');
      var scrollIdle = null;

      function setActiveByIndex(i) {
        activate(i);
      }

      /* Keep dot state synced with the swipe scroll position on mobile. */
      main.addEventListener('scroll', function () {
        if (scrollIdle) cancelAnimationFrame(scrollIdle);
        scrollIdle = requestAnimationFrame(function () {
          var w = main.clientWidth || 1;
          var i = Math.round(main.scrollLeft / w);
          if (i < 0) i = 0;
          if (i >= slides.length) i = slides.length - 1;
          setActiveByIndex(i);
        });
      }, { passive: true });

      /* Click a dot → scroll the gallery to that slide (mobile). On
         desktop the gallery is opacity-faded so this is just a class swap. */
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var w = main.clientWidth || 1;
          if (main.scrollWidth > main.clientWidth) {
            main.scrollTo({ left: i * w, behavior: 'smooth' });
          }
          setActiveByIndex(i);
        });
      });
    }

    if (slides.length) activate(0);
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', function () {
        var activeIndex = Array.prototype.findIndex.call(slides, function (s) {
          return s.classList.contains('is-active');
        });
        activate(activeIndex < 0 ? 0 : activeIndex);
      });
    }
  });

  // ----- Lightbox (shared, with zoom + pan) -----
  var lightbox = null;
  var lbImg = null;
  var lbZoomBtn = null;
  var lbIsZoomed = false;
  var lbOffsetX = 0;
  var lbOffsetY = 0;
  var lbDrag = null;
  var ZOOM_SCALE = 2.4;

  var icoZoomIn  = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>';
  var icoZoomOut = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>';

  function applyZoomTransform() {
    if (!lbImg) return;
    if (lbIsZoomed) {
      lbImg.style.transform = 'translate(' + lbOffsetX + 'px, ' + lbOffsetY + 'px) scale(' + ZOOM_SCALE + ')';
    } else {
      lbImg.style.transform = '';
    }
  }

  function setZoom(on) {
    lbIsZoomed = !!on;
    lbOffsetX = 0;
    lbOffsetY = 0;
    if (lbImg) lbImg.classList.toggle('is-zoomed', lbIsZoomed);
    if (lbZoomBtn) {
      lbZoomBtn.innerHTML = lbIsZoomed ? icoZoomOut : icoZoomIn;
      lbZoomBtn.setAttribute('aria-label', lbIsZoomed ? 'Zoom out' : 'Zoom in');
    }
    applyZoomTransform();
  }

  function buildLightbox() {
    lightbox = document.createElement('div');
    lightbox.className = 'pv2-lightbox';
    lightbox.innerHTML =
      '<div class="pv2-lightbox__inner"><img alt="" /></div>' +
      '<button type="button" class="pv2-lightbox__zoom" aria-label="Zoom in">' + icoZoomIn + '</button>' +
      '<button type="button" class="pv2-lightbox__close" aria-label="Close">' +
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12"/><path d="M18 6 6 18"/></svg>' +
      '</button>';
    document.body.appendChild(lightbox);
    lbImg = lightbox.querySelector('img');
    lbZoomBtn = lightbox.querySelector('.pv2-lightbox__zoom');

    // Close: only when clicking the backdrop or the X button — not the image.
    lightbox.addEventListener('click', function (e) {
      if (e.target.closest('.pv2-lightbox__zoom')) return;
      if (e.target === lightbox || e.target.closest('.pv2-lightbox__close')) {
        lightbox.classList.remove('is-open');
        setZoom(false);
      }
    });

    // Zoom button: toggle.
    lbZoomBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      setZoom(!lbIsZoomed);
    });

    // Click on the image: toggle zoom.
    lbImg.addEventListener('click', function (e) {
      e.stopPropagation();
      if (!lbDrag) setZoom(!lbIsZoomed);
    });

    // Drag to pan when zoomed.
    lbImg.addEventListener('mousedown', function (e) {
      if (!lbIsZoomed) return;
      e.preventDefault();
      lbDrag = { x: e.clientX, y: e.clientY, ox: lbOffsetX, oy: lbOffsetY, moved: false };
      lbImg.classList.add('is-grabbing');
    });
    document.addEventListener('mousemove', function (e) {
      if (!lbDrag) return;
      var dx = e.clientX - lbDrag.x;
      var dy = e.clientY - lbDrag.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) lbDrag.moved = true;
      lbOffsetX = lbDrag.ox + dx;
      lbOffsetY = lbDrag.oy + dy;
      applyZoomTransform();
    });
    document.addEventListener('mouseup', function () {
      if (!lbDrag) return;
      var moved = lbDrag.moved;
      lbImg.classList.remove('is-grabbing');
      // If the user actually dragged, swallow the upcoming click so it
      // doesn't toggle zoom off after a pan.
      if (moved) {
        var swallow = function (ev) { ev.stopPropagation(); ev.preventDefault(); lbImg.removeEventListener('click', swallow, true); };
        lbImg.addEventListener('click', swallow, true);
      }
      lbDrag = null;
    });

    // ESC closes.
    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        lightbox.classList.remove('is-open');
        setZoom(false);
      }
    });
  }

  function openLightbox(src) {
    if (!lightbox) buildLightbox();
    lbImg.src = src;
    setZoom(false);
    lightbox.classList.add('is-open');
  }

  // ----- Multi-option variant picker (auto-splits multi-criteria values) -----
  document.querySelectorAll('[data-pv2-buy]').forEach(function (buy) {
    // Idempotency guard: if this IIFE ever runs more than once against the
    // same buy block (theme-editor section reload, ajax re-render, etc.),
    // skip — otherwise click handlers stack and the qty stepper increments
    // N times per click.
    if (buy.dataset.pv2BuyInited === '1') return;
    buy.dataset.pv2BuyInited = '1';

    var optsRoot = buy.querySelector('[data-pv2-opts]');
    var dataNode = buy.querySelector('[data-pv2-variants]');
    if (!optsRoot || !dataNode) return;

    var variants;
    try { variants = JSON.parse(dataNode.textContent || '[]'); }
    catch (e) { variants = []; }
    if (!variants.length) return;

    var formId = buy.getAttribute('data-pv2-form-id');
    var hidden = formId ? document.querySelector('#' + formId + ' [name="id"]') : null;
    var skuTarget = buy.querySelector('[data-pv2-sku]');
    var priceTarget = buy.querySelector('[data-pv2-price]');
    var priceMetaSep = buy.querySelector('[data-pv2-price-meta-sep]');
    var priceMetaUom = buy.querySelector('[data-pv2-price-meta-uom]');
    var priceCompare = buy.querySelector('[data-pv2-price-compare]');
    var boxNote = buy.querySelector('[data-pv2-box-note]');
    var boxNoteText = buy.querySelector('[data-pv2-box-note-text]');

    // A variant with no price is not for sale yet. Shopify has no per-variant
    // draft status, so price is the gate: treat an unpriced variant as one we
    // don't carry, exactly like a combo with no SKU. The moment a real price
    // lands, the option value un-greys on its own — no deploy needed.
    function isSellable(v) { return (v.price_cents || 0) > 0; }

    // Build initial selection from the first sellable, available variant.
    var initial = variants.find(function (v) { return v.available && isSellable(v); })
      || variants.find(function (v) { return v.available; })
      || variants[0];
    var selected = initial.options.slice();

    // Build flat per-option metadata. No further splitting — each Shopify
    // option is rendered as its own selector with its raw values.
    var optGroups = Array.prototype.slice.call(optsRoot.querySelectorAll('[data-pv2-opt]'));
    var optionMeta = optGroups.map(function (group, idx) {
      return {
        group: group,
        position: idx,
        name: group.getAttribute('data-opt-name') || ('Option ' + (idx + 1)),
      };
    });

    // Find the variant matching the current `selected` option values.
    function findVariant() {
      return variants.find(function (v) {
        return v.options.every(function (val, i) { return val === selected[i]; });
      });
    }

    // Option values for a group, in render order.
    function valuesAt(position) {
      var g = optionMeta[position] && optionMeta[position].group;
      if (!g) return [];
      return Array.prototype.map.call(
        g.querySelectorAll('[data-opt-value]'),
        function (b) { return b.getAttribute('data-opt-value'); }
      );
    }

    // Does a real variant exist with `value` at `position`, given the values
    // already chosen in the EARLIER positions? Earlier options stay freely
    // switchable; later options are constrained by the ones before them —
    // the same cascade Shopify's own variant pickers use. A combo we don't
    // carry (no SKU) simply isn't in `variants`, so it resolves to false —
    // and an unpriced variant is treated the same way (see isSellable).
    function valueHasVariant(position, value) {
      return variants.some(function (v) {
        if (!isSellable(v)) return false;
        if (v.options[position] !== value) return false;
        for (var i = 0; i < position; i++) {
          if (v.options[i] !== selected[i]) return false;
        }
        return true;
      });
    }

    // After a change at `changedPos`, walk the later options and, if the
    // current pick there no longer maps to a real variant (e.g. switching
    // Style orphaned the old Size), snap it to the first value that does.
    // Guarantees `selected` is always a combo we actually sell.
    function reconcile(changedPos) {
      for (var q = changedPos + 1; q < optionMeta.length; q++) {
        if (!valueHasVariant(q, selected[q])) {
          var vals = valuesAt(q);
          for (var k = 0; k < vals.length; k++) {
            if (valueHasVariant(q, vals[k])) { selected[q] = vals[k]; break; }
          }
        }
      }
    }

    // Grey out + disable any option value that can't form a real variant with
    // the current selection. These are native <button> elements, so the
    // `disabled` attribute blocks the click outright — the customer can never
    // land on a combo we don't have.
    function refreshDisabled() {
      optionMeta.forEach(function (meta) {
        var pos = meta.position;
        meta.group.querySelectorAll('[data-opt-value]').forEach(function (b) {
          if (valueHasVariant(pos, b.getAttribute('data-opt-value'))) {
            b.removeAttribute('disabled');
            b.removeAttribute('aria-disabled');
            b.classList.remove('is-unavailable');
          } else {
            b.setAttribute('disabled', '');
            b.setAttribute('aria-disabled', 'true');
            b.classList.add('is-unavailable');
          }
        });
      });
    }

    // Update active button styling.
    function paintActive() {
      optionMeta.forEach(function (meta) {
        var current = selected[meta.position];
        meta.group.querySelectorAll('[data-opt-value]').forEach(function (b) {
          b.classList.toggle('is-active', b.getAttribute('data-opt-value') === current);
        });
        var cur = meta.group.querySelector('[data-opt-current]');
        if (cur) cur.textContent = current || '';
      });
    }

    // Update the right-side price/SKU/UOM display from the current variant.
    function updateDisplay() {
      var v = findVariant();
      if (!v) return;
      if (hidden) {
        hidden.value = v.id;
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (skuTarget && v.sku) skuTarget.textContent = 'SKU ' + v.sku;
      if (priceTarget && v.price_money) priceTarget.textContent = v.price_money;
      if (priceMetaUom) priceMetaUom.textContent = v.uom_display || '';
      if (priceMetaSep) priceMetaSep.textContent = v.uom_display ? ' · ' : '';
      if (priceCompare) {
        if (v.compare_money) {
          priceCompare.textContent = v.compare_money;
          priceCompare.removeAttribute('hidden');
        } else {
          priceCompare.textContent = '';
          priceCompare.setAttribute('hidden', '');
        }
      }
      if (boxNote && boxNoteText) {
        // Compute the box-note text client-side from raw price + qty
        // instead of trusting the Liquid-prebuilt v.box_note string.
        // Liquid metafield access has been flaky for the box_quantity
        // wrapper object — by doing the math in JS we sidestep all of
        // that and just need the two numbers in the JSON.
        var palletNote = buy.querySelector('[data-pv2-pallet-note]');
        var priceCents = parseInt(v.price_cents, 10);
        var qty = parseInt(v.box_qty, 10);
        var hasPriced = isFinite(priceCents) && priceCents > 0;
        var hasQty = isFinite(qty) && qty > 1;
        if (v.is_each || !hasPriced || !hasQty) {
          boxNote.setAttribute('hidden', '');
          boxNoteText.innerHTML = '';
          if (palletNote) palletNote.setAttribute('hidden', '');
        } else {
          var perUnitCents = Math.round(priceCents / qty);
          var fmt = function (cents) { return '$' + (cents / 100).toFixed(2); };
          var uomCap = (v.uom || 'box').charAt(0).toUpperCase() + (v.uom || 'box').slice(1);
          var noteHtml = uomCap + ' of ' + qty + ' = '
                       + '<strong>' + fmt(priceCents) + '</strong> · '
                       + '<strong>' + fmt(perUnitCents) + '</strong> per unit.';
          boxNoteText.innerHTML = noteHtml;
          boxNote.removeAttribute('hidden');
          if (palletNote) palletNote.removeAttribute('hidden');
        }
      }
    }

    // Bind clicks
    optionMeta.forEach(function (meta) {
      meta.group.querySelectorAll('[data-opt-value]').forEach(function (b) {
        b.addEventListener('click', function () {
          if (b.hasAttribute('disabled')) return;
          selected[meta.position] = b.getAttribute('data-opt-value');
          reconcile(meta.position); // keep later options on a combo we sell
          refreshDisabled();        // re-grey what the new selection rules out
          paintActive();
          updateDisplay();
        });
      });
    });

    // Initial paint to reflect any server-side selected variant.
    paintActive();
    refreshDisabled();
    updateDisplay();

    // qty stepper
    var qtyInput = buy.querySelector('[data-pv2-qty]');
    var minus = buy.querySelector('[data-pv2-qty-minus]');
    var plus = buy.querySelector('[data-pv2-qty-plus]');
    if (qtyInput && minus && plus) {
      minus.addEventListener('click', function () {
        var v = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, v - 1);
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
      plus.addEventListener('click', function () {
        var v = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = v + 1;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  });

  // ----- Detail tabs (also handles the buy-box Overview/Description tabs) -----
  document.querySelectorAll('[data-pv2-tabs]').forEach(function (tabs) {
    // Idempotency guard — same reason as [data-pv2-buy].
    if (tabs.dataset.pv2TabsInited === '1') return;
    tabs.dataset.pv2TabsInited = '1';

    // Scope queries to direct child structure, so a [data-pv2-tabs] inside
    // another [data-pv2-tabs] doesn't pull both sets of buttons together.
    var btns = Array.prototype.filter.call(
      tabs.querySelectorAll('[data-pv2-tab-btn]'),
      function (b) { return b.closest('[data-pv2-tabs]') === tabs; }
    );
    var panels = Array.prototype.filter.call(
      tabs.querySelectorAll('[data-pv2-tab-panel]'),
      function (p) { return p.closest('[data-pv2-tabs]') === tabs; }
    );
    btns.forEach(function (b, i) {
      b.addEventListener('click', function () {
        btns.forEach(function (x) { x.classList.remove('is-active'); });
        panels.forEach(function (x) { x.classList.remove('is-active'); });
        b.classList.add('is-active');
        if (panels[i]) panels[i].classList.add('is-active');
      });
    });
  });

  // ----- FAQ accordion -----
  document.querySelectorAll('[data-pv2-faq] [data-pv2-faq-btn]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var item = btn.closest('[data-pv2-faq-item]');
      if (item) item.classList.toggle('is-open');
    });
  });

  // ----- Size guide -----
  document.querySelectorAll('[data-pv2-sg]').forEach(function (sg) {
    var slider = sg.querySelector('[data-pv2-sg-slider]');
    var num = sg.querySelector('[data-pv2-sg-num]');
    var bandsEl = sg.querySelector('[data-pv2-sg-bands]');
    var indicator = sg.querySelector('[data-pv2-sg-indicator]');
    var indicatorLabel = sg.querySelector('[data-pv2-sg-indicator-label]');
    var recName = sg.querySelector('[data-pv2-sg-rec-name]');
    var recMeta = sg.querySelector('[data-pv2-sg-rec-meta]');
    var bands = Array.prototype.map.call(sg.querySelectorAll('[data-pv2-sg-band]'), function (el) {
      return {
        el: el,
        idx: parseInt(el.getAttribute('data-pv2-band-idx'), 10),
        lo: parseFloat(el.getAttribute('data-pv2-band-lo')),
        hi: parseFloat(el.getAttribute('data-pv2-band-hi')),
        name: el.getAttribute('data-pv2-band-name'),
        imp: el.getAttribute('data-pv2-band-imp'),
        met: el.getAttribute('data-pv2-band-met'),
        sku: el.getAttribute('data-pv2-band-sku')
      };
    });
    if (bands.length === 0 || !slider) return;
    var min = parseFloat(slider.min);
    var max = parseFloat(slider.max);
    var span = max - min;
    var rows = sg.querySelectorAll('[data-pv2-sg-row]');
    var rowBtns = sg.querySelectorAll('[data-pv2-sg-select]');

    function update(val) {
      val = Math.max(min, Math.min(max, parseFloat(val) || min));
      if (slider.value != val) slider.value = val;
      if (num.value != val) num.value = val;
      var pct = ((val - min) / span) * 100;
      if (indicator) indicator.style.left = pct + '%';
      if (indicatorLabel) indicatorLabel.textContent = val.toFixed(2) + '"';
      var match = null;
      bands.forEach(function (b) {
        if (val >= b.lo && val <= b.hi) match = b;
      });
      if (!match) {
        // nearest by midpoint
        match = bands.reduce(function (best, b) {
          var midB = (b.lo + b.hi) / 2;
          var midBest = (best.lo + best.hi) / 2;
          return Math.abs(midB - val) < Math.abs(midBest - val) ? b : best;
        });
      }
      bands.forEach(function (b) { b.el.classList.toggle('is-active', b === match); });
      rows.forEach(function (r) {
        var ri = parseInt(r.getAttribute('data-pv2-sg-row'), 10);
        r.classList.toggle('is-active', ri === match.idx);
      });
      rowBtns.forEach(function (rb) {
        var ri = parseInt(rb.getAttribute('data-pv2-sg-select'), 10);
        var active = ri === match.idx;
        rb.classList.toggle('is-active', active);
        rb.textContent = active ? 'Selected' : 'Select';
      });
      if (recName) {
        var prefix = sg.getAttribute('data-pv2-sg-prefix');
        recName.innerHTML = (prefix ? prefix + ' · ' : '') + '<span>' + match.name + '</span>';
      }
      if (recMeta) {
        var sku = match.sku ? ' · SKU ' + match.sku : '';
        recMeta.textContent = match.imp + ' · ' + match.met + sku;
      }
    }

    slider.addEventListener('input', function () { update(slider.value); });
    num.addEventListener('change', function () { update(num.value); });
    bands.forEach(function (b) {
      b.el.addEventListener('click', function () {
        update((b.lo + b.hi) / 2);
        scrollToBuy(b.idx);
      });
    });
    rowBtns.forEach(function (rb) {
      rb.addEventListener('click', function () {
        var ri = parseInt(rb.getAttribute('data-pv2-sg-select'), 10);
        var match = bands[ri];
        if (!match) return;
        update((match.lo + match.hi) / 2);
        scrollToBuy(ri);
      });
    });

    function scrollToBuy(idx) {
      // Match the size-guide band's SKU to a variant, then click each
      // option chip for that variant's option values.
      var band = bands[idx];
      if (!band || !band.sku) return;
      var dataNode = document.querySelector('[data-pv2-variants]');
      if (!dataNode) return;
      var vs;
      try { vs = JSON.parse(dataNode.textContent || '[]'); } catch (e) { return; }
      var match = vs.find(function (v) { return v.sku === band.sku; });
      if (!match) return;
      var opts = document.querySelectorAll('[data-pv2-opt]');
      opts.forEach(function (group, i) {
        var target = match.options[i];
        if (!target) return;
        var btn = group.querySelector('[data-opt-value="' + String(target).replace(/(["\\])/g, '\\$1') + '"]');
        if (btn) btn.click();
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    update(slider.value);
  });

  // ----- Sticky buy bar -----
  document.querySelectorAll('[data-pv2-stickybar]').forEach(function (bar) {
    var ref = document.querySelector(bar.getAttribute('data-pv2-stickybar-ref') || '[data-pv2-buy]');
    if (!ref) return;
    var onScroll = function () {
      var r = ref.getBoundingClientRect();
      bar.classList.toggle('is-visible', r.bottom < 80);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  });

  // ----- Description read-more toggle (Fix #7) -----
  document.querySelectorAll('[data-pv2-desc-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var wrap = btn.closest('[data-pv2-desc-wrap]');
      if (!wrap) return;
      var clamped = wrap.querySelector('[data-pv2-desc]');
      var full = wrap.querySelector('[data-pv2-desc-full]');
      var moreLabel = btn.querySelector('[data-pv2-desc-toggle-more]');
      var lessLabel = btn.querySelector('[data-pv2-desc-toggle-less]');
      if (!full) return;
      var isExpanded = !full.hidden;
      full.hidden = isExpanded;
      if (clamped) clamped.classList.toggle('pv2-buy__desc--clamped', isExpanded);
      if (moreLabel) moreLabel.hidden = !isExpanded;
      if (lessLabel) lessLabel.hidden = isExpanded;
    });
  });

  // ----- Smooth scroll to size guide (Fix #14) -----
  // Products without a size-guide section fall back to the size table in the
  // Specifications tab; with neither on the page the link is hidden.
  var sizeGuide = document.getElementById('size-guide');
  var specsTab = document.querySelector('#product-details [data-pv2-tab-specs]');
  document.querySelectorAll('[data-pv2-size-help]').forEach(function (link) {
    if (!sizeGuide && !specsTab) { link.hidden = true; return; }
    if (!sizeGuide) {
      link.setAttribute('href', '#product-details');
      link.textContent = link.hasAttribute('data-pv2-size-help-short')
        ? 'Size chart'
        : 'Not sure which size? See the size chart \u2192';
    }
    link.addEventListener('click', function (e) {
      e.preventDefault();
      if (sizeGuide) {
        sizeGuide.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      specsTab.click();
      document.getElementById('product-details').scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  // ----- Related carousel nav -----
  document.querySelectorAll('[data-pv2-related]').forEach(function (rel) {
    var scroller = rel.querySelector('[data-pv2-related-scroller]');
    var prev = rel.querySelector('[data-pv2-related-prev]');
    var next = rel.querySelector('[data-pv2-related-next]');
    if (!scroller) return;
    if (prev) prev.addEventListener('click', function () { scroller.scrollBy({ left: -300, behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { scroller.scrollBy({ left: 300, behavior: 'smooth' }); });
  });

})();
